import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    createFilterOptions,
    Menu,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { ReactComponent as SearchIcon } from '../../../static/icons/searchicon.svg'
import { getActiveAgentListForAdmin, getgroups } from '../../../services/api-service'
import { ReactComponent as ChipCloseIcon } from '../../../static/icons/chipclose.svg'
import CustomizedCheckbox from '../../../components/admin/common/CustomCheckBox'
import {
    showErrorNotification,
    showInfoNotification,
    showWarningNotification
} from '../../../components/admin/common/NotiService'

export const DropDownIcon = () => {
    return (
        <div style={{ display: "flex", alignContent: "center", marginTop: "4px", marginRight: "4px", marginLeft: "4px" }}>
            <img
                src={`${process.env.PUBLIC_URL}/icons/downArrowDropDown.svg`}
                alt="edit"
            ></img>
        </div>
    )
};

export default function AgentSelector({
    selectedAgents,
    setSelectedAgents,
    selectedGroups,
    setSelectedGroups,
    selected
}) {

    const [agentGroupsList, setAgentGroupsList] = useState([])
    const [agentsList, setAgentsList] = useState([]);
    const [type, setType] = useState('Agents')
    const [anchorEl, setAnchorEl] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState({
        agents: false,
        groups: false
    })

    const isAllAgentsSelected = selectedAgents.length === agentsList.length && agentsList.length > 0;
    const isAllGroupsSelected = selectedGroups.length === agentGroupsList.length && agentGroupsList.length > 0;
    const customLimitTags = useMemo(() => {
        if (selected && selected.length && selected.length > 6) {
            return 6;
        } else {
            return selected.length;
        }
    }, [selected])

    const autoCompleteListRef = useRef(null);
    const open = Boolean(anchorEl);
    const types = ['Agents', 'Agent Groups'];

    useEffect(() => {
        getAgents();
        getGroupsList();
    }, []);

    const getAgents = async () => {
        setLoading((prev) => ({ ...prev, agents: true }))
        try {
            const resp = await getActiveAgentListForAdmin();
            if (resp.Status === "Success" && resp.Data && resp.Data.length && resp.Data.length > 0) {
                setAgentsList(resp.Data.reduce((acc, agent) => {
                    acc.push({
                        id: agent.id, type: "Agent", agentName: agent.agentName + " ~ " + agent.agentId, agentId: agent.agentId
                    })
                    return acc;
                }, []))
            } else {
                console.log("getAgents failed ~ resp:", resp)
            }
            setLoading((prev) => ({ ...prev, agents: false }))
        }
        catch (err) {
            console.log(err)
            setLoading((prev) => ({ ...prev, agents: false }))
        }
    }

    const getGroupsList = async () => {
        setLoading((prev) => ({ ...prev, groups: true }))
        try {
            const resp = await getgroups();
            if (resp.Status === "Success" && resp.Data && resp.Data.length && resp.Data.length > 0) {
                setAgentGroupsList(resp.Data.reduce((acc, group) => {
                    acc.push({ GroupName: group.GroupName, id: group.SeqID, type: "Agent Group" })
                    return acc;
                }, []))
            } else {
                console.log("getGroups failed ~ resp:", resp)
            }
            setLoading((prev) => ({ ...prev, groups: false }))
        }
        catch (err) {
            console.log(err)
            setLoading((prev) => ({ ...prev, groups: false }))
        }
    }

    const handleClose = () => {
        setAnchorEl(null);
        setInputValue('');
        setLimit(10);
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight) {
            setLimit((prev) => prev + 10);
        }
    }

    const handleSelectAll = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (type === 'Agents') {
            // Get currently visible/filtered agents
            const filteredAgents = inputValue
                ? agentsList.filter(agent =>
                    agent.agentName.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (agent.agentId && agent.agentId.toLowerCase().includes(inputValue.toLowerCase()))
                )
                : agentsList;

            // Add filtered agents to selection (avoiding duplicates)
            const existingIds = new Set(selectedAgents.map(agent => agent.id));
            const newAgents = filteredAgents.filter(agent => !existingIds.has(agent.id));
            setSelectedAgents([...selectedAgents, ...newAgents]);
        } else {
            // Get currently visible/filtered groups
            const filteredGroups = inputValue
                ? agentGroupsList.filter(group =>
                    group.GroupName.toLowerCase().includes(inputValue.toLowerCase())
                )
                : agentGroupsList;

            // Add filtered groups to selection (avoiding duplicates)
            const existingIds = new Set(selectedGroups.map(group => group.id));
            const newGroups = filteredGroups.filter(group => !existingIds.has(group.id));
            setSelectedGroups([...selectedGroups, ...newGroups]);
        }
    }

    const handleClearFiltered = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (type === 'Agents') {
            // If no agents selected, do nothing
            if (!selectedAgents || selectedAgents.length === 0) return;

            // Get the filtered agents that match the search text
            const filteredAgents = inputValue
                ? agentsList.filter(agent =>
                    agent.agentName.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (agent.agentId && agent.agentId.toLowerCase().includes(inputValue.toLowerCase()))
                )
                : agentsList;

            // If no agents match the filter, do nothing
            if (filteredAgents.length === 0) return;

            // Create a set of IDs for faster lookup
            const filteredIds = new Set(filteredAgents.map(agent => agent.id));

            // Remove matching agents from selection (keep only non-filtered agents)
            const newSelection = selectedAgents.filter(agent => !filteredIds.has(agent.id));

            setSelectedAgents(newSelection);
        } else {
            // If no groups selected, do nothing
            if (!selectedGroups || selectedGroups.length === 0) return;

            // Get the filtered groups that match the search text
            const filteredGroups = inputValue
                ? agentGroupsList.filter(group =>
                    group.GroupName.toLowerCase().includes(inputValue.toLowerCase())
                )
                : agentGroupsList;

            // If no groups match the filter, do nothing
            if (filteredGroups.length === 0) return;

            // Create a set of IDs for faster lookup
            const filteredIds = new Set(filteredGroups.map(group => group.id));

            // Remove matching groups from selection (keep only non-filtered groups)
            const newSelection = selectedGroups.filter(group => !filteredIds.has(group.id));

            setSelectedGroups(newSelection);
        }
    }

    // Function to generate options with action buttons row
    const getOptions = () => {
        const baseOptions = type === 'Agents'
            ? [...agentsList]
            : [...agentGroupsList];

        // Only add the action buttons if we have options to select
        if (baseOptions.length > 0) {
            const actionButton = type === 'Agents'
                ? { id: 'action-buttons-row', agentName: 'ACTION_BUTTONS_ROW', type: "Agent" }
                : { id: 'action-buttons-row', GroupName: 'ACTION_BUTTONS_ROW', type: "Agent Group" };

            return [actionButton, ...baseOptions];
        }

        return baseOptions;
    }

    return (
        <Box className="oz-admin-bulk-update-content-body-controls-item">
            <Typography fontSize={"12px"} fontWeight={400} color={"#212121"}>
                Choose agents<span style={{ color: "red" }}>*</span>
            </Typography>
            <Box className='agent-selector' onClick={(e) => { setAnchorEl(e.currentTarget); }}>
                <Box className='agent-selector-selected '>
                    {selected.length > 0 ? selected.map((option, index) => (
                        customLimitTags > index ? <Tooltip title={option.agentName || option.GroupName}>
                            <Chip key={option.id || option.GroupName} label={option.agentName || option.GroupName} onDelete={(e) => {
                                if (option.type === "Agent") setSelectedAgents(selectedAgents.filter(agent => agent.id !== option.id));
                                if (option.type === "Agent Group") setSelectedGroups(selectedGroups.filter(group => group.id !== option.id));
                            }} deleteIcon={<ChipCloseIcon />} />  </Tooltip> :
                            customLimitTags === index ? <Tooltip title={`+${selected.length - customLimitTags}`} > <Chip key={option.id || option.GroupName} label={`+${selected.length - customLimitTags}`} /> </Tooltip> : null
                    )) : <Typography fontSize={"14px"} fontWeight={400} color={"#3D4C5E"}>Select</Typography>}
                </Box>
                <DropDownIcon />
            </Box>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '8px',
                        marginTop: '10px',
                        width: "inherit",
                        border: "1px solid #D6D9DC",
                        boxShadow: "none",
                        minHeight: "300px",
                        p: 1,
                        pt: 0
                    }
                }}>
                <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
                    <Autocomplete
                        value={type}
                        size={"small"}
                        onChange={(e, newValue) => {
                            setType(newValue || "Agents");
                            setInputValue('');
                            setLimit(10);
                        }}
                        options={types}
                        popupIcon={<DropDownIcon />}
                        clearIcon={null}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: "14px", color: "#3D4C5E",
                                        borderRadius: '8px 0px 0px 8px',
                                        borderLeft: "none",
                                        minHeight: "40px",
                                    },
                                    minHeight: "40px",
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ color: "#47586E", fontSize: "14px", fontWeight: 500 }}>
                                {option}
                            </Box>
                        )}
                        componentsProps={{
                            paper: { sx: { border: "1px solid #D6D6D7", marginTop: "6px", borderRadius: "8px" } },
                            popupIndicator: {
                                sx: {
                                    transform: "none",
                                    marginRight: "0px",
                                    marginTop: "6px",
                                    padding: "0px",
                                }
                            }
                        }}
                        sx={{
                            width: "165px",
                            height: "40px",
                            fontSize: "14px",
                            color: "#3D4C5E",
                            borderRadius: "8px 0px 0px 8px !important",
                            borderRight: "none !important",
                        }}
                    />
                    <Autocomplete
                        size='small'
                        sx={{ minWidth: "324px" }}
                        multiple
                        value={type === 'Agents' ? selectedAgents : selectedGroups}
                        onChange={(e, value, reason, details) => {
                            // Skip the ACTION_BUTTONS_ROW from being selected
                            const filteredValue = value.filter(option =>
                                !(option.agentName === 'ACTION_BUTTONS_ROW' ||
                                    option.GroupName === 'ACTION_BUTTONS_ROW')
                            );

                            if (filteredValue.some(option => option.id === 'select-all')) {
                                // Handle "Select All" item click separately
                                handleSelectAll();
                            } else {
                                // Update the appropriate selection
                                if (type === 'Agents') {
                                    // Keep the current selection if nothing changed
                                    if (JSON.stringify(filteredValue) !== JSON.stringify(selectedAgents)) {
                                        setSelectedAgents(filteredValue);
                                    }
                                } else if (type === 'Agent Groups') {
                                    // Keep the current selection if nothing changed
                                    if (JSON.stringify(filteredValue) !== JSON.stringify(selectedGroups)) {
                                        setSelectedGroups(filteredValue);
                                    }
                                }
                            }
                        }}
                        loading={type === 'Agents' ? loading.agents : loading.groups}
                        loadingText={type === 'Agents' ? 'Loading Agents' : 'Loading Groups'}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                        }}
                        options={getOptions()}
                        popupIcon={<SearchIcon />}
                        clearIcon={null}
                        getOptionLabel={(option) => {
                            // Don't display label for action buttons row
                            if (option.agentName === 'ACTION_BUTTONS_ROW' || option.GroupName === 'ACTION_BUTTONS_ROW') {
                                return '';
                            }
                            return option.agentName || option.GroupName || "";
                        }}
                        disableCloseOnSelect={true}
                        componentsProps={{
                            paper: { sx: { border: "1px solid #D6D6D7", marginTop: "6px", borderRadius: "8px" } },
                            popupIndicator: {
                                sx: {
                                    transform: "none",
                                    marginRight: "0px",
                                    marginTop: "6px",
                                    padding: "0px",
                                }
                            }
                        }}
                        role={"listbox"}
                        ListboxProps={{
                            ref: autoCompleteListRef,
                            onScroll: handleScroll,
                            sx: { maxHeight: "224px" }
                        }}
                        onInputChange={(e, value, reason) => {
                            if (reason === "reset") return;
                            setInputValue(value)
                            if (limit > 10) { setLimit(10) }
                            if (value.includes(',')) {
                                setLoading((prev) => ({ ...prev, agents: true }))
                                try {
                                    const agentIds = value.split(',')
                                    if (type === 'Agents' && agentIds.length > 1) {
                                        let agents = agentIds.map(agentId => agentsList.find(agent => agent.agentId === agentId)).filter(agent => agent)
                                        if (agents.length > 0) {
                                            let alreadySelectedAgents = [];
                                            if (selectedAgents.length > 0) {
                                                alreadySelectedAgents = selectedAgents.map(agent => agent.agentId)
                                            }
                                            agents = agents.filter(agent => !alreadySelectedAgents.includes(agent.agentId))
                                            setSelectedAgents([...selectedAgents, ...agents])
                                            showInfoNotification('Agents added successfully')
                                        } else {
                                            showWarningNotification('No agents found with the given agent id(s)')
                                        }
                                        setInputValue('')
                                    } else {
                                        let valuesWithoutComma = value.replace(/,/g, '')
                                        setInputValue(valuesWithoutComma)
                                    }
                                }
                                catch (err) {
                                    console.log(err);
                                    setLoading((prev) => ({ ...prev, agents: false }))
                                    showErrorNotification('Failed to add multiple agents')
                                }
                            } else {
                                setInputValue(value)
                            }
                        }}
                        inputValue={inputValue}
                        isOptionEqualToValue={(option, value) => option["id"] === value["id"]}
                        filterOptions={
                            (options, params) => {
                                const customFilter = createFilterOptions({
                                    matchFrom: 'any',
                                    ignoreCase: true,
                                    limit: limit,
                                });

                                // Always keep the action buttons row
                                const actionButtonsRow = options.find(option =>
                                    option.agentName === 'ACTION_BUTTONS_ROW' ||
                                    option.GroupName === 'ACTION_BUTTONS_ROW'
                                );

                                // Filter actual data options
                                const dataOptions = options.filter(opt =>
                                    opt.agentName !== 'ACTION_BUTTONS_ROW' &&
                                    opt.GroupName !== 'ACTION_BUTTONS_ROW'
                                );

                                // Apply filtering to data options
                                let filtered = customFilter(dataOptions, params);

                                // Return action button first if we have results and an action button
                                if (filtered.length > 0 && actionButtonsRow) {
                                    return [actionButtonsRow, ...filtered];
                                }

                                return filtered;
                            }
                        }
                        renderOption={(props, option, { selected, inputValue }) => {
                            // Render the action buttons row
                            if (option.agentName === 'ACTION_BUTTONS_ROW' || option.GroupName === 'ACTION_BUTTONS_ROW') {
                                return (
                                    <li {...props}
                                        style={{
                                            padding: 0,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            borderBottom: '1px solid #E5E5E5',
                                            height: 'auto !important'
                                        }}
                                        // These event handlers are critical to prevent the dropdown from closing
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        <Button
                                            sx={{
                                                textTransform: 'none',
                                                flex: 1,
                                                justifyContent: 'center',
                                                fontFamily: 'Roboto',
                                                fontWeight: 400,
                                                fontSize: "14px",
                                                color: '#3D8BF8',
                                                borderRight: '1px solid #E5E5E5',
                                                minWidth: 'auto',
                                                borderRadius: 0
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSelectAll(e);
                                                return false;
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            Select All
                                        </Button>
                                        <Button
                                            style={{
                                                textTransform: 'none',
                                                flex: 1,
                                                justifyContent: 'center',
                                                fontFamily: 'Roboto',
                                                fontWeight: 400,
                                                fontSize: "14px",
                                                color: '#3D8BF8',
                                                minWidth: 'auto',
                                                borderRadius: 0
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleClearFiltered(e);
                                                return false;
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            Clear All
                                        </Button>
                                    </li>
                                );
                            }

                            // Render normal option
                            return (
                                <Box id={option.id || ""} {...props} className="oz-admin-bulk-update-content-body-controls-item-select-option">
                                    <CustomizedCheckbox checked={option.id === 'select-all' ? (type === 'Agents' ? isAllAgentsSelected : isAllGroupsSelected) : selected} />
                                    <Tooltip title={option.agentName || option.GroupName} >
                                        <Typography sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", }} fontSize={"14px"} fontWeight={400} color={"#47586E"} textTransform={"capitalize"}>
                                            {option.agentName || option.GroupName}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            );
                        }}
                        renderInput={(params) => <TextField placeholder='Select'
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: "14px", color: "#3D4C5E",
                                    borderRadius: '0px 8px 8px 0px',
                                    borderLeft: "none",
                                    minHeight: "40px"
                                },
                                minHeight: "40px",
                            }}
                            {...params}
                        />}
                        renderTags={(value, getTagProps) => <></>}
                    />
                </Box>
            </Menu>
        </Box>
    )
}